// Sorting files dropped on a spec (web/src/pages/specs/SpecFiles.tsx): a PCB export's Gerber layers and drill files
// become one zip per board, its BOM and placement files a slot each, a dropped zip is sorted by what's inside it, and
// each upload is matched to the slot it's a new version of. Pure: the browser walks folders, lists zips and zips
// (drop.ts); this decides. Also what a part type's spec needs, in words ("Gerbers ✓ · BOM + placement missing").
import { kicadProject, projectPath } from "./kicad.ts";
import { missingFiles, PART_TYPE_LABELS, type PartType, packagingOf, partNeeds } from "./projects.ts";
import { fileSlotName, guessFileKind, pcbFileRole, type SpecFileKind, withoutDateRev } from "./specs.ts";

/** What a dropped file is, from its name (and path: a Mac zip's resource forks are junk). */
export type DropRole = "layer" | "drill" | "extra" | "placement" | "bom" | "step" | "firmware" | "zip" | "source" | "junk" | "other";

/** A dropped file: `path` is where it was in a dropped folder; `entries`, a zip's file names when the browser looked. */
export type DroppedFile = { name: string; size: number; path?: string; entries?: string[] };

/** A spec's file slot; `filename` is its current upload's (none: nothing uploaded yet). */
export type DropSlot = { id: string; name: string; kind: SpecFileKind; filename?: string | null };

/** One file in a planned upload: a dropped file, or a file inside a dropped zip (`entry`). `name`: its name in the zip made. */
export type DropPart = { file: number; entry?: string; name: string };

export type DropGroup = {
  /** Stays the same while the drop is open, so the choices made on a group stick. */
  key: string;
  /** A new slot's name when the file's own wouldn't say what it is (KiCad's BOM "ProGCC3S.csv" → "ProGCC3S BOM"). */
  name?: string;
  /** The file uploaded: a dropped file's own name, or the zip made ("ProGCC3S Gerbers.zip"). */
  filename: string;
  /** What it is, in plain words: "12 Gerber layers + drill", "Placement from ProGCC3S.zip". Empty for an ordinary file. */
  what: string;
  kind: SpecFileKind;
  /** The files it's made of: zipped together into `filename` when `zip`, else the one part goes as it is. */
  parts: DropPart[];
  zip: boolean;
  /** Something to check before uploading ("Only 1 layer: is the rest missing?"). */
  warning?: string;
  /** The slot it's a new version of unless people choose otherwise; null: a new file. */
  slot: string | null;
  /** Why it's left out unless people choose otherwise (a zip of the same layers dropped alongside them). */
  leaveOut?: string;
  /**
   * A dropped zip with Gerbers and BOM or placement files, or several boards' Gerbers: whether this plan splits it
   * (`keepWhole` in the context); `boards`, how many boards' Gerbers it holds when more than one.
   */
  splits?: { file: number; split: boolean; boards?: number };
  /** KiCad projects --- the board whose manufacturing files HHL Ops makes from this upload (a KiCad project zip). */
  kicad?: string;
};

export type DropContext = {
  /** The item's name: guessFileKind's context for a zip nobody could look inside. */
  title?: string;
  /** Dropped zips (by index) to upload whole rather than split into the Gerbers zip and the BOM/placement files. */
  keepWhole?: ReadonlySet<number>;
  /** The slot the files were dropped on: the group of its kind no other slot has by name goes there. */
  target?: string;
  /** KiCad projects --- the server can run KiCad: a dropped KiCad project is uploaded for HHL Ops to make the files from. */
  kicad?: boolean;
};

const baseName = (path: string) => path.split(/[\\/]/).pop() ?? path;
const dirOf = (path: string) => path.split(/[\\/]/).slice(0, -1).join("/");
const stemOf = (path: string) => baseName(path).replace(/\.[a-z0-9_]{1,7}$/i, "");
/** The extension a slot is matched by (the wiki importer's `slotKey` rule). */
const extOf = (name: string) => (/\.[a-z0-9]{1,5}$/i.exec(name)?.[0] ?? "").toLowerCase();
const norm = (s: string) => s.toLowerCase().replace(/[\s_]+/g, "");
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
const sameText = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/**
 * Folders nobody means to upload, not walked either (drop.ts): hidden ones (.git), a Mac zip's __MACOSX, KiCad's
 * backups, Altium's History (backups), __Previews and Project Logs.
 */
export const junkFolder = (name: string) => /^\.(?!\.?$)|^__macosx$|-backups$|^history$|^__previews$|^project logs for /i.test(name);
/**
 * Files nobody means to upload: system files, lock files (Office's ~$, KiCad's ~….lck), backups and autosaves (KiCad's
 * -bak and _autosave-, EAGLE's .b#1), and the design tools' caches and local settings (.kicad_prl, fp-info-cache).
 */
const JUNK_NAME = /^(\.|~)|^(thumbs\.db|desktop\.ini|fp-info-cache)$|\.(lck|bak|tmp|kicad_prl)$|-bak$|^_autosave-|\.[bsl]#\d$|docpreview$/;
/**
 * A board's design files (KiCad, Altium, EAGLE, EasyEDA): offered zipped together but left out by default, since a CM
 * builds from the Gerbers, BOM and placement.
 */
const SOURCE =
  /\.(kicad_pro|kicad_pcb|kicad_sch|kicad_sym|kicad_mod|kicad_dru|kicad_wks|pro|sch|brd|lbr|pcbdoc|schdoc|prjpcb|prjpcbstructure|outjob|pcblib|schlib|intlib|harness|epro|eprj)$|^(sym|fp)-lib-table$/;

export function dropRole(path: string): DropRole {
  const folders = path.split(/[\\/]/);
  const name = (folders.pop() ?? "").toLowerCase();
  if (!name || folders.some(junkFolder) || JUNK_NAME.test(name)) return "junk";
  const pcb = pcbFileRole(name);
  if (pcb) return pcb;
  if (SOURCE.test(name)) return "source";
  if (/\.(step|stp|iges|igs|stl|x_t|sldprt|f3d)$/.test(name)) return "step";
  if (/\.(uf2|bin|hex|elf|dfu)$/.test(name)) return "firmware";
  if (name.endsWith(".zip")) return "zip";
  return "other";
}

// Names: the board, its revisions ----------------------------------------------------------------------

/** A date or revision inside a name ("ProGCC3S_R5-top-pos", "ProGCC3S R5 11.02.2026-top-pos"): what a new export changes. */
const DATE_REV = /[\s_-]+(\d{4}[./-]\d{1,2}[./-]\d{1,2}|\d{1,2}[./-]\d{1,2}[./-]\d{2,4}|(r|rev|v)\.?\s?\d+[a-z]?)(?=$|[\s_.-])/gi;
/** A name without its dates, revisions and a browser's " (1)", wherever they are. */
const sansDateRev = (name: string) =>
  withoutDateRev(name.replace(/\s*\(\d{1,3}\)$/, ""))
    .replace(DATE_REV, "")
    .trim();
/** How slot names compare: no case, spaces or underscores (the wiki importer's `slotKey`), no dates or revisions. */
const nameKey = (name: string) => norm(sansDateRev(name)) || norm(name);

/** The board in an export file's name, the way PCB tools write it (the board, then the file's role; or the role, then the board). */
const BOARD_FIRST = /^(.+?)[\s_-]+(gerbers?|(((top|bottom|bot|all|front|back)[\s_-]*)?(pos|cpl|pnp|xy|centroid))|bom|bill[\s_-]?of[\s_-]?materials)$/;
const BOARD_LAST = /^(bom|bill[\s_-]?of[\s_-]?materials|cpl|pnp|xy|centroid|pick[\s_-]?(and[\s_-]?)?place([\s_-]+for)?)[\s_-]+(.+)$/;

/**
 * The board an export file is named after ("ProGCC3S Gerbers R5.zip", "ProGCC3S_R5-top-pos.csv", "ProGCC3S-top.pos",
 * "BOM_ProGCC3S.csv", "Pick Place for ProGCC3S.txt" → "progcc3s"); "" when its name doesn't say ("Main_R5.zip").
 */
function boardOf(filename: string): string {
  const s = sansDateRev(stemOf(filename)).toLowerCase();
  const pos = extOf(filename) === ".pos" ? /^(.+?)[\s_-]+(top|bottom|bot|all|front|back)$/.exec(s) : null;
  const board = pos?.[1] ?? BOARD_FIRST.exec(s)?.[1] ?? BOARD_LAST.exec(s)?.at(-1) ?? "";
  return board.replace(/[\s_.-]+/g, "");
}

// The Gerbers zip's name --------------------------------------------------------------------------

/** KiCad's and Altium's layer and drill names after the project's ("ProGCC3S-F_Cu" → "ProGCC3S"). */
const LAYER_SUFFIX =
  /[-_ ]((f|b|in\d+)[._](cu|mask|paste|silks?|silkscreen|fab|crtyd|courtyard|adhes|adhesive)|edge[._]cuts|(user|dwgs|cmts|eco\d)[._][a-z0-9]+|[a-z]+[._]user|margin|job|n?pth(-drl(_map)?)?|drl(_map)?|(round|slot)holes)$/i;

/** The project name a set of layer files shares ("ProGCC3S-F_Cu.gtl", "ProGCC3S.drl" → "ProGCC3S"); "" if none. */
function commonBase(names: string[]): string {
  const stems = names.map((n) => stemOf(n).replace(LAYER_SUFFIX, ""));
  if (!stems.length) return "";
  let prefix = stems[0];
  for (const s of stems) while (!s.toLowerCase().startsWith(prefix.toLowerCase())) prefix = prefix.slice(0, -1);
  // Whole words only: "GCU" and "GCU2" share no name.
  const whole = stems.every((s) => s.length === prefix.length || /[-_ .]/.test(s[prefix.length])) || /[-_ .]$/.test(prefix);
  return (whole ? prefix : prefix.replace(/[^-_ .]*$/, "")).replace(/[-_ .]+$/, "");
}

const GERBER_WORDS = /[\s_-]*(gerbers?|gerber[\s_-]?files|fab|outputs?)$/i;
const GENERIC = /^(gerbers?|gerber[\s_-]?files|fab|fabrication|outputs?|cam|plots?|production|manufacturing|exports?|pcb|drills?|files)$/i;

/**
 * The Gerbers zip's file name from the first useful candidate (the layers' shared name, then folder names): "<name>
 * Gerbers.zip", with any date or revision moved to the end ("GCU_R5" → "GCU Gerbers R5.zip"), so the slot's name stays
 * "GCU Gerbers" from one revision to the next. "Gerbers.zip" when nothing names it.
 */
export function gerbersZipName(candidates: string[]): string {
  for (const c of candidates) {
    const s = c.replace(/^(project outputs for|gerbers?)[\s_-]+/i, "").trim();
    let name = s;
    for (let before = ""; before !== name; ) {
      before = name;
      name = withoutDateRev(name).replace(GERBER_WORDS, "");
    }
    name = name.replace(/_+/g, " ").trim();
    if (!name || GENERIC.test(name)) continue;
    const tail = s
      .slice(name.length)
      .replace(/gerbers?|gerber[\s_-]?files|fab|outputs?/gi, " ")
      .replace(/^[\s_-]+|[\s_-]+$/g, "")
      .replace(/\s+/g, " ");
    return `${name} Gerbers${tail ? ` ${tail}` : ""}.zip`;
  }
  return "Gerbers.zip";
}

/** Each set's zip name from its candidates; sets that would share one are named by their folders first ("Main/board.GTL"). */
function namesFor(sets: { candidates: string[]; byFolder: string[] }[]): string[] {
  const first = sets.map((s) => gerbersZipName(s.candidates));
  return first.map((n, k) => (first.indexOf(n) === first.lastIndexOf(n) ? n : gerbersZipName(sets[k].byFolder)));
}

// Gerber sets ----------------------------------------------------------------------------------------

type GerberSet = { roles: DropRole[]; base: string; layers: number; drills: number };

/** The roles of a set of files that may hold Gerbers: a .txt with the layers' name is the drill file (Altium, EAGLE). */
function gerberSet(names: string[]): GerberSet {
  const roles = names.map(dropRole);
  const base = commonBase(names.filter((_, k) => roles[k] === "layer" || roles[k] === "drill"));
  for (let k = 0; k < names.length; k++) if (roles[k] === "other" && base && /\.txt$/i.test(names[k]) && sameText(stemOf(names[k]), base)) roles[k] = "drill";
  return { roles, base, layers: roles.filter((r) => r === "layer").length, drills: roles.filter((r) => r === "drill").length };
}

const setText = (s: GerberSet) => (s.layers ? `${plural(s.layers, "Gerber layer")}${s.drills ? " + drill" : ""}` : plural(s.drills, "drill file"));
const setWarning = (s: GerberSet) =>
  !s.layers
    ? "Only drill files: are the Gerber layers missing?"
    : s.layers === 1
      ? "Only 1 layer: is the rest missing?"
      : !s.drills
        ? "No drill file: is it missing?"
        : undefined;

/** Whether a file may belong in a Gerbers zip: a layer, drill file or report, or a .txt (Altium's and EAGLE's drill files). */
const mayBeGerbers = (path: string, role = dropRole(path)) =>
  role === "layer" || role === "drill" || role === "extra" || (role === "other" && /\.txt$/i.test(path));

/** A board's Gerber files: `members` index the paths given to gerberSets; `dir` is their common folder, `key` the board's own. */
type SetOf = { members: number[]; set: GerberSet; dir: string; key: string };

/** The folder every path is in ("" when none is shared). */
const commonDir = (paths: string[]) =>
  paths.map(dirOf).reduce((a, d) => {
    let shared = a;
    while (shared && d !== shared && !d.startsWith(`${shared}/`)) shared = dirOf(shared);
    return shared;
  });

/**
 * The boards among files that may be Gerbers (in order): one set per folder holding layers (two boards' folders are
 * two zips), the drill files and reports of folders without layers joining the board they belong to (Altium's Gerber
 * and NC Drill folders, EAGLE's GerberFiles and DrillFiles), a folder of drill files no board takes a set of its own.
 * `rest`: what's in no set (a .txt that isn't a drill file, reports with no board to join).
 */
function gerberSets(paths: string[]): { sets: SetOf[]; rest: number[] } {
  const byDir = new Map<string, number[]>();
  for (const [k, p] of paths.entries()) byDir.set(dirOf(p), [...(byDir.get(dirOf(p)) ?? []), k]);
  const folders = [...byDir].map(([dir, list]) => ({ dir, list, set: gerberSet(list.map((k) => paths[k])) }));
  const boards = folders.filter((f) => f.set.layers).map((f) => ({ ...f, list: [...f.list] }));
  const lists = [...boards];
  const rest: number[] = [];
  for (const f of folders) {
    if (f.set.layers) continue;
    // Its drill files' name and the board's agree (or one has none); a board named like it first, else the only board.
    const fits = boards.filter((b) => !f.set.base || !b.set.base || sameText(f.set.base, b.set.base));
    const named = fits.filter((b) => f.set.base && sameText(f.set.base, b.set.base));
    const board = named.length === 1 ? named[0] : boards.length === 1 ? fits[0] : undefined;
    if (board) board.list.push(...f.list);
    else if (f.set.drills) lists.push({ ...f, list: [...f.list] });
    else rest.push(...f.list);
  }
  const sets = lists.map(({ dir, list }) => {
    list.sort((a, b) => a - b);
    const set = gerberSet(list.map((k) => paths[k]));
    rest.push(...list.filter((_, n) => set.roles[n] === "other"));
    const members = list.filter((_, n) => set.roles[n] !== "other");
    return { members, set, dir: commonDir(members.map((k) => paths[k])), key: dir };
  });
  return { sets: sets.sort((a, b) => a.members[0] - b.members[0]), rest: rest.sort((a, b) => a - b) };
}

/** KiCad 8's BOM is "<project>.csv": a spreadsheet named like a board's Gerbers is its BOM. */
const projectBom = (name: string, bases: Set<string>) => /\.(csv|xlsx?)$/i.test(name) && bases.has(nameKey(stemOf(name)));
const basesOf = (sets: SetOf[]) => new Set(sets.filter((s) => s.set.base).map((s) => nameKey(s.set.base)));

/** Names inside a zip made from `names`: each file's own name, its path where two share one. */
function zipNames(paths: string[]): string[] {
  const count = new Map<string, number>();
  for (const p of paths) count.set(baseName(p).toLowerCase(), (count.get(baseName(p).toLowerCase()) ?? 0) + 1);
  return paths.map((p) => ((count.get(baseName(p).toLowerCase()) ?? 0) > 1 ? p : baseName(p)));
}

// The plan ---------------------------------------------------------------------------------------------

/**
 * What to upload for a drop, deterministically: each board's Gerber layers and drill files (with their reports) zipped
 * into one file, dropped zips sorted by what's in them (Gerbers with BOM or placement files, or several boards', split
 * unless kept whole), BOM and placement files a slot each, everything else as it is with a guessed kind. A file dropped
 * again (the same name in the same folder) replaces the one dropped before. Each group is matched to the slot it's a
 * new version of: first the slot with its name or holding a file of its name (dates and revisions aside) and its
 * extension; then the row it was dropped on; then, for a Gerbers zip, the one slot named "… Gerbers" or else the only
 * Gerbers (pcb_fab) slot, and for the only BOM or placement file dropped the one slot whose upload is one, unless
 * their names say they're another board's. Gerbers come first, then BOM and placement, then the rest.
 */
export function planDrop(files: DroppedFile[], slots: DropSlot[], context: DropContext = {}): DropGroup[] {
  const pathOf = (i: number) => files[i].path ?? files[i].name;
  const latest = new Map(files.map((_, i) => [pathOf(i), i]));
  const sortKey = (i: number) => pathOf(i).toLowerCase();
  const leftovers = kicadLeftovers(files.map((_, i) => pathOf(i))); // KiCad projects ---
  const order = files
    .map((_, i) => i)
    .filter((i) => latest.get(pathOf(i)) === i && dropRole(pathOf(i)) !== "junk" && !leftovers.has(pathOf(i)))
    .sort((a, b) => (sortKey(a) < sortKey(b) ? -1 : sortKey(a) > sortKey(b) ? 1 : a - b));
  const place = new Map(order.map((i, k) => [i, k]));
  const groups: Planned[] = [];

  const maybe = order.filter((i) => mayBeGerbers(pathOf(i)));
  const singles = order.filter((i) => !mayBeGerbers(pathOf(i)));
  const { sets, rest } = gerberSets(maybe.map(pathOf));
  singles.push(...rest.map((k) => maybe[k]));
  const dirsOf = (s: SetOf) => s.dir.split("/").filter(Boolean).reverse();
  const names = namesFor(sets.map((s) => ({ candidates: [s.set.base, ...dirsOf(s)], byFolder: [...dirsOf(s), s.set.base] })));
  sets.forEach((s, n) => {
    const members = s.members.map((k) => maybe[k]);
    const inZip = zipNames(members.map(pathOf));
    groups.push({
      group: {
        key: `set:${s.key}`,
        filename: names[n],
        what: setText(s.set),
        kind: "pcb_fab",
        parts: members.map((file, k) => ({ file, name: inZip[k] })),
        zip: true,
        warning: setWarning(s.set),
        slot: null,
      },
      gerbers: true,
      loose: true,
      layerNames: new Set(members.filter((i) => dropRole(pathOf(i)) === "layer").map((i) => files[i].name.toLowerCase())),
    });
  });

  const bases = basesOf(sets);
  singles.sort((a, b) => (place.get(a) ?? 0) - (place.get(b) ?? 0));
  // KiCad projects --- with KiCad on the server (`context.kicad`), a board's KiCad project goes as one zip HHL Ops makes
  // the Gerbers, drill, placement, BOM and schematic from; exports dropped with it are left out (KiCad makes them anew).
  const designFiles = singles.filter((i) => dropRole(pathOf(i)) === "source");
  const project = context.kicad ? kicadProject(designFiles.map(pathOf)) : null;
  if (project) {
    const members = designFiles.filter((i) => project.files.includes(pathOf(i)));
    groups.push({
      group: {
        key: "kicad",
        filename: `${project.board} KiCad project.zip`,
        what: `KiCad project, ${plural(members.length, "file")}: HHL Ops makes the Gerbers, drill, placement, BOM and schematic from it with KiCad, and reads the board's components from it`,
        kind: "kicad",
        parts: members.map((file) => ({ file, name: projectPath(pathOf(file), project.folder) })),
        zip: true,
        slot: null,
        kicad: project.board,
      },
      role: "source",
      kicad: true,
    });
  }
  // The design files go together, left out unless people choose to keep them.
  const source = designFiles.filter((i) => !project?.files.includes(pathOf(i)));
  if (source.length) {
    const one = source.length === 1;
    const base = sets.find((s) => s.set.base)?.set.base || stemOf(files[source[0]].name);
    const inZip = zipNames(source.map(pathOf));
    groups.push({
      group: {
        key: "source",
        filename: one ? files[source[0]].name : `${base} design files.zip`,
        what: `${plural(source.length, "design file")}: ${source
          .slice(0, 3)
          .map((i) => files[i].name)
          .join(", ")}${source.length > 3 ? ` and ${source.length - 3} more` : ""}`,
        kind: "design",
        parts: source.map((file, k) => ({ file, name: inZip[k] })),
        zip: !one,
        slot: null,
        leaveOut: "Design files: a CM builds from the Gerbers, BOM and placement",
      },
      role: "source",
    });
  }
  for (const i of singles) {
    if (designFiles.includes(i)) continue; // in the KiCad project or the design files
    const f = files[i];
    const found = dropRole(pathOf(i));
    const role = found === "other" && projectBom(f.name, bases) ? "bom" : found;
    const single = (kind: SpecFileKind, what = ""): DropGroup => ({
      key: `file:${i}`,
      filename: f.name,
      what,
      kind,
      parts: [{ file: i, name: f.name }],
      zip: false,
      slot: null,
    });
    if (role === "placement" || role === "bom") {
      const group = single("pcba", role === "bom" ? "BOM" : "Placement");
      if (role === "bom" && !/bom|bill/i.test(f.name)) group.name = `${fileSlotName(f.name)} BOM`;
      groups.push({ group, role });
    } else if (role === "zip" && f.entries) groups.push(...zipGroups(i, f, context));
    else groups.push({ group: single(guessFileKind(f.name, context.title)), role });
  }

  // A zip of the very layers dropped alongside it is the same Gerbers twice.
  const loose = groups.filter((g) => g.loose);
  const same = (a: Set<string>, b?: Set<string>) => a.size === b?.size && [...a].every((n) => b.has(n));
  for (const g of groups)
    if (g.gerbers && !g.loose && !g.group.zip && g.layerNames && loose.some((s) => same(g.layerNames as Set<string>, s.layerNames)))
      g.group.leaveOut = "The same layers as the loose files dropped with it";

  // KiCad projects --- what KiCad makes from the project, dropped too, is an older export of it.
  if (project)
    for (const g of groups)
      if (!g.kicad && (g.gerbers || g.group.kind === "pcb_fab" || g.group.kind === "pcba"))
        g.group.leaveOut ??= "KiCad makes this from the project, so this export would be out of date";

  // The KiCad project first, then Gerbers, other fab files (a lone netlist), BOM and placement, the rest.
  const rank = (g: Planned) => (g.kicad ? -1 : g.gerbers ? 0 : g.group.kind === "pcb_fab" ? 1 : g.group.kind === "pcba" ? 2 : 3);
  groups.sort((a, b) => rank(a) - rank(b));
  matchSlots(groups, slots, context.target);
  return groups.map((g) => g.group);
}

/**
 * What a drop leaves out without asking (system, backup and cache files, and KiCad's leftovers beside a project): their
 * paths, each once.
 */
export const skippedFiles = (files: DroppedFile[]): string[] => {
  const paths = files.map((f) => f.path ?? f.name);
  const leftovers = kicadLeftovers(paths);
  return [...new Set(paths.filter((p) => dropRole(p) === "junk" || leftovers.has(p)))];
};

/**
 * KiCad projects --- KiCad's leftovers beside a KiCad 6+ project, never needed (the project's own files say it all; Mitch,
 * 2026-11-01: a dropped project folder should "properly filter the non-necessary files"): the KiCad 5 files it was
 * converted from (<board>.sch, <board>.pro, -cache.lib, -rescue.lib) and the netlist and BOM-plugin exports eeschema
 * writes beside it (<board>.xml, .net, .cmp), out of date once the schematic changes. Skipped like junk.
 */
export function kicadLeftovers(paths: readonly string[]): Set<string> {
  const stems = new Map<string, Set<string>>();
  for (const p of paths) {
    const m = /^(.*?)([^/\\]+)\.kicad_(pro|sch)$/i.exec(p);
    if (m) stems.set(m[1].toLowerCase(), (stems.get(m[1].toLowerCase()) ?? new Set()).add(m[2].toLowerCase()));
  }
  const out = new Set<string>();
  for (const p of paths) {
    const m = /^(.*?)([^/\\]+)\.(sch|pro|xml|net|cmp|lib)$/i.exec(p);
    const project = m && stems.get(m[1].toLowerCase());
    if (!m || !project) continue;
    const stem = m[2].toLowerCase();
    if (m[3].toLowerCase() === "lib" ? /-(cache|rescue)$/.test(stem) : project.has(stem)) out.add(p);
  }
  return out;
}

/** A group being planned: the role it was sorted by, and for Gerbers its layers' names (`loose`: made from dropped files). */
type Planned = { group: DropGroup; role?: DropRole; gerbers?: boolean; loose?: boolean; layerNames?: Set<string>; kicad?: boolean };

/** A dropped zip, sorted by what's inside it. */
function zipGroups(i: number, f: DroppedFile, context: DropContext): Planned[] {
  const entries = (f.entries ?? []).filter((e) => dropRole(e) !== "junk");
  const whole = (kind: SpecFileKind, what: string): DropGroup => ({
    key: `file:${i}`,
    filename: f.name,
    what,
    kind,
    parts: [{ file: i, name: f.name }],
    zip: false,
    slot: null,
  });
  const maybe = entries.map((_, k) => k).filter((k) => mayBeGerbers(entries[k]));
  const { sets } = gerberSets(maybe.map((k) => entries[k]));
  const bases = basesOf(sets);
  const roles = entries.map((e) => {
    const role = dropRole(e);
    return role === "other" && projectBom(e, bases) ? "bom" : role;
  });
  const isPcba = (k: number) => roles[k] === "placement" || roles[k] === "bom";
  const pcba = entries.filter((_, k) => isPcba(k));
  const pcbaText = (names: string[]) => {
    const bom = names.some((n) => roles[entries.indexOf(n)] === "bom");
    const placement = names.some((n) => roles[entries.indexOf(n)] === "placement");
    return bom && placement ? "BOM + placement" : bom ? "BOM" : "placement";
  };
  const layersOf = (list: number[]) => new Set(list.filter((k) => roles[k] === "layer").map((k) => baseName(entries[k]).toLowerCase()));

  if (sets.some((s) => s.set.layers)) {
    const boards = sets.length > 1 ? sets.length : undefined;
    if ((!pcba.length && !boards) || context.keepWhole?.has(i)) {
      const gerbers = boards ? `Gerbers for ${boards} boards` : setText(sets[0].set);
      const group = whole("pcb_fab", `Zip: ${gerbers}${pcba.length ? `, ${pcbaText(pcba)}` : ""}`);
      group.warning = boards ? undefined : setWarning(sets[0].set);
      if (pcba.length || boards) group.splits = { file: i, split: false, ...(boards ? { boards } : {}) };
      return [{ group, gerbers: true, layerNames: layersOf(entries.map((_, k) => k)) }];
    }
    // Split: each board's Gerbers zip keeps everything else in its folder (the rest go with the first, so nothing is
    // lost); the BOM and placement files a slot each.
    const owner = new Map<number, number>();
    sets.forEach((s, n) => {
      for (const m of s.members) owner.set(maybe[m], n);
    });
    entries.forEach((e, k) => {
      if (owner.has(k) || isPcba(k)) return;
      const d = dirOf(e);
      const holders = sets.map((s, n) => ({ n, dir: s.dir })).filter((s) => !s.dir || d === s.dir || d.startsWith(`${s.dir}/`));
      owner.set(k, holders.sort((a, b) => b.dir.length - a.dir.length)[0]?.n ?? 0);
    });
    const dirsOf = (s: SetOf) => s.dir.split("/").filter(Boolean).reverse();
    const names = namesFor(sets.map((s) => ({ candidates: [s.set.base, stemOf(f.name)], byFolder: [...dirsOf(s), s.set.base, stemOf(f.name)] })));
    return [
      ...sets.map((s, n): Planned => {
        const list = entries.map((_, k) => k).filter((k) => owner.get(k) === n);
        const inZip = zipNames(list.map((k) => entries[k]));
        return {
          group: {
            key: `zip:${i}:gerbers:${s.key}`,
            filename: names[n],
            what: `${setText(s.set)} from ${f.name}`,
            kind: "pcb_fab",
            parts: list.map((k, m) => ({ file: i, entry: entries[k], name: inZip[m] })),
            zip: true,
            warning: setWarning(s.set),
            slot: null,
            splits: { file: i, split: true, ...(boards ? { boards } : {}) },
          },
          gerbers: true,
          layerNames: layersOf(list),
        };
      }),
      ...pcba.map((entry): Planned => {
        const role = roles[entries.indexOf(entry)];
        return {
          group: {
            key: `zip:${i}:${entry}`,
            filename: baseName(entry),
            what: `${role === "bom" ? "BOM" : "Placement"} from ${f.name}`,
            kind: "pcba",
            parts: [{ file: i, entry, name: baseName(entry) }],
            zip: false,
            slot: null,
          },
          role,
        };
      }),
    ];
  }
  const count = (role: DropRole) => roles.filter((r) => r === role).length;
  if (entries.length && count("step") === entries.length) return [{ group: whole("step", `Zip: ${plural(entries.length, "3D model")}`) }];
  if (count("firmware")) return [{ group: whole("firmware", "Zip: firmware") }];
  if (pcba.length && pcba.length === entries.length) return [{ group: whole("pcba", `Zip: ${pcbaText(pcba)}`) }];
  return [{ group: whole(guessFileKind(f.name, context.title), entries.length ? `Zip of ${plural(entries.length, "file")}` : "") }];
}

/** Sets each group's `slot`, the slot it's a new version of by default (see planDrop); one group per slot. */
function matchSlots(groups: Planned[], slots: DropSlot[], targetId?: string) {
  const live = groups.filter((g) => !g.group.leaveOut);
  const taken = new Set<string>();
  const claim = (g: Planned | undefined, s: DropSlot | undefined) => {
    if (!g || !s) return;
    g.group.slot = s.id;
    taken.add(s.id);
  };
  const free = () => slots.filter((s) => !taken.has(s.id));
  const sameExt = (g: DropGroup, s: DropSlot) => !s.filename || extOf(s.filename) === extOf(g.filename);

  // 1. Its slot by name: the slot's own, or its upload's (a slot renamed since), whatever the dates and revisions.
  for (const g of live) {
    const key = nameKey(fileSlotName(g.group.filename));
    claim(
      g,
      free().find((s) => sameExt(g.group, s) && (nameKey(s.name) === key || (!!s.filename && nameKey(fileSlotName(s.filename)) === key))),
    );
  }

  // 2. The row the files were dropped on, unless a group has it by name: the group of its kind with no slot yet, one
  // like its upload first (a BOM dropped with placement files onto the BOM's row goes there).
  const target = targetId && !taken.has(targetId) ? slots.find((s) => s.id === targetId) : undefined;
  if (target) {
    const open = live.filter((g) => !g.group.slot && g.group.kind === target.kind);
    const role = target.filename ? dropRole(target.filename) : undefined;
    const only = live.length === 1 && !live[0].group.slot ? live[0] : undefined;
    claim(open.find((g) => g.role === role) ?? open[0] ?? only, target);
  }

  // 3. The one slot of its sort, unless the two names say they're different boards' ("Buttons Gerbers.zip" isn't a new
  // version of "ProGCC3S Gerbers"). A BOM or placement file only when it's the only one of its sort dropped.
  const alone = (role?: DropRole) => (role === "bom" || role === "placement") && groups.filter((g) => g.role === role).length === 1;
  for (const g of live) {
    if (g.group.slot) continue;
    const fits = free().filter((s) => sameExt(g.group, s));
    let only: DropSlot | undefined;
    if (g.gerbers && extOf(g.group.filename) === ".zip") {
      const named = fits.filter((s) => /gerbers?$/i.test(s.name.trim()));
      const fab = fits.filter((s) => s.kind === "pcb_fab");
      only = named.length === 1 ? named[0] : !named.length && fab.length === 1 ? fab[0] : undefined;
    } else if (alone(g.role)) {
      const like = fits.filter((s) => s.filename && dropRole(s.filename) === g.role);
      only = like.length === 1 ? like[0] : undefined;
    }
    const board = boardOf(g.group.filename);
    const theirs = only ? boardOf(only.filename ?? `${only.name}${extOf(g.group.filename)}`) : "";
    claim(g, board && theirs && board !== theirs ? undefined : only);
  }
}

// Dropped zips' dates ------------------------------------------------------------------------------------

/**
 * Each entry's date in a zip, by name (as fflate names them): its DOS date and time from the central directory, read
 * as local time the way fflate writes them back, so a file taken out of a dropped zip keeps its own date in the zip
 * made. Empty when the zip can't be read this way (zip64, damaged).
 */
export function zipEntryDates(zip: Uint8Array): Map<string, number> {
  const out = new Map<string, number>();
  const u16 = (o: number) => zip[o] | (zip[o + 1] << 8);
  const u32 = (o: number) => (u16(o) | (u16(o + 2) << 16)) >>> 0;
  let end = zip.length - 22;
  while (end >= 0 && u32(end) !== 0x06054b50) if (zip.length - end-- > 65557) return out;
  if (end < 0) return out;
  const utf8 = new TextDecoder();
  let at = u32(end + 16);
  for (let k = u16(end + 10); k > 0 && at + 46 <= zip.length && u32(at) === 0x02014b50; k--) {
    const time = u16(at + 12);
    const date = u16(at + 14);
    const raw = zip.subarray(at + 46, at + 46 + u16(at + 28));
    const name = u16(at + 8) & 2048 ? utf8.decode(raw) : String.fromCharCode(...raw);
    out.set(name, new Date(1980 + (date >> 9), ((date >> 5) & 15) - 1, date & 31, time >> 11, (time >> 5) & 63, (time & 31) * 2).getTime());
    at += 46 + u16(at + 28) + u16(at + 30) + u16(at + 32);
  }
  return out;
}

// What a part type's spec needs ------------------------------------------------------------------------

/** The kinds of a spec's files that count: in the draft, with an upload (as the projects' readiness counts them). */
export const kindsWithUploads = (files: { kind: string; in_draft: boolean; current_version_id: string | null }[]) =>
  new Set(files.filter((f) => f.in_draft && f.current_version_id).map((f) => f.kind));

export type FileNeedState = { label: string; have: boolean; optional: boolean };
/** What decides a spec's files: the part type, and a packaging part's kind and printing (partNeeds). */
export type NeedsItem = Parameters<typeof partNeeds>[0];

/** Each file the item's spec should have, and whether it does (partNeeds, missingFiles). */
export function fileNeedStates(item: NeedsItem, kinds: ReadonlySet<string>): FileNeedState[] {
  const { required, optional } = missingFiles(item, kinds);
  const missing = new Set([...required, ...optional]);
  return partNeeds(item).files.map((n) => ({ label: n.label, have: !missing.has(n), optional: !!n.optional }));
}

/** "Gerbers ✓ · BOM + placement missing"; "" when the item expects no files. */
export const fileNeeds = (item: NeedsItem, kinds: ReadonlySet<string>) =>
  fileNeedStates(item, kinds)
    .map((n) => `${n.label} ${n.have ? "✓" : n.optional ? "(optional)" : "missing"}`)
    .join(" · ");

/** What its files are called: "PCBA", "Printed box". */
export const needsLabel = (item: NeedsItem) => packagingOf(item)?.title ?? PART_TYPE_LABELS[item.part_type as PartType] ?? "";

const NEED_WORDS: Record<string, string> = {
  Gerbers: "Gerbers (zip of the layers and drill files)",
  STEP: "a STEP model",
  Drawing: "a 2D drawing",
  Dieline: "a dieline",
  "Print artwork": "the print artwork",
  "Datasheet or drawing": "a datasheet or drawing",
};
const andList = (items: string[]) => (items.length < 2 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`);

/**
 * What an empty spec of this item should get, for the empty Files tab: "A PCBA spec needs Gerbers (zip of the layers
 * and drill files) and BOM + placement. Drop the whole export folder; it's sorted for you." A packaging part with no
 * files to get says what it needs instead ("No file: write the words to print in its spec"). "" for none.
 */
export function filesHint(item: NeedsItem): string {
  const needs = partNeeds(item).files;
  const pack = packagingOf(item);
  if (pack && !needs.length) return pack.says;
  const type = needsLabel(item);
  const label = /^[A-Z]+$/.test(type) ? type : type.toLowerCase();
  const a = /^[aeiou]/i.test(label) ? "An" : "A";
  const words = (list: typeof needs) => andList(list.map((n) => NEED_WORDS[n.label] ?? n.label));
  const required = needs.filter((n) => !n.optional);
  const optional = needs.filter((n) => n.optional);
  const out: string[] = [];
  if (required.length) out.push(`${a} ${label} spec needs ${words(required)}.`);
  else if (optional.length) out.push(`${a} ${label} spec could use ${words(optional)}.`);
  if (needs.some((n) => n.kinds.includes("pcb_fab"))) out.push("Drop the whole export folder; it's sorted for you.");
  return out.join(" ");
}
